import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { listDiscovered, listSources, type DiscoveredCompany, type MarketingSource } from "../api/marketing.ts";

const STATUS_COLOR: Record<string, string> = {
  NEW: "bg-blue-900/40 text-blue-300",
  RESEARCHING: "bg-yellow-900/40 text-yellow-300",
  QUALIFIED: "bg-green-900/40 text-green-300",
  DISQUALIFIED: "bg-gray-700/40 text-gray-400",
  IMPORTED: "bg-purple-900/40 text-purple-300",
};

export function MarketingDiscoveryPage() {
  const { token } = useAuth();
  const [sources, setSources] = useState<MarketingSource[]>([]);
  const [companies, setCompanies] = useState<DiscoveredCompany[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    if (token == null) return;
    setLoading(true);
    Promise.all([
      listSources(token, { limit: 50 }),
      listDiscovered(token, { status: statusFilter || undefined, limit: 50 }),
    ])
      .then(([s, d]) => {
        setSources(s.items);
        setCompanies(d.items);
        setTotal(d.total);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [token, statusFilter]);

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Lead Discovery</h1>
        </div>

        {error != null && <p className="text-red-400">{error}</p>}

        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide">Discovery Sources ({sources.length})</h2>
          {sources.length === 0 && !loading && <p className="text-gray-500 text-sm">No sources configured.</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sources.map((s) => (
              <div key={s.id} className="rounded-lg border border-gray-800 p-4 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-white">{s.name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${s.enabled ? "bg-green-900/40 text-green-300" : "bg-gray-700 text-gray-400"}`}>
                    {s.enabled ? "Active" : "Disabled"}
                  </span>
                </div>
                <p className="text-xs text-gray-500">{s.type} · Last run: {s.lastRunAt ? new Date(s.lastRunAt).toLocaleDateString() : "Never"}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide">Discovered Companies ({total.toLocaleString()})</h2>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm bg-gray-900 border border-gray-700 rounded px-2 py-1 text-gray-300"
            >
              <option value="">All Statuses</option>
              <option value="NEW">New</option>
              <option value="RESEARCHING">Researching</option>
              <option value="QUALIFIED">Qualified</option>
              <option value="DISQUALIFIED">Disqualified</option>
              <option value="IMPORTED">Imported</option>
            </select>
          </div>

          {loading && <p className="text-gray-400 text-sm">Loading…</p>}

          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="border-b border-gray-800 text-gray-400">
                  <th className="pb-2 pr-4">Company</th>
                  <th className="pb-2 pr-4">Domain</th>
                  <th className="pb-2 pr-4">Industry</th>
                  <th className="pb-2 pr-4">Score</th>
                  <th className="pb-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr key={c.id} className="border-b border-gray-800/50 hover:bg-gray-900/50">
                    <td className="py-2 pr-4 text-white font-medium">{c.name}</td>
                    <td className="py-2 pr-4 text-gray-400">{c.domain}</td>
                    <td className="py-2 pr-4 text-gray-400">{c.industry ?? "—"}</td>
                    <td className="py-2 pr-4 text-gray-300">{c.score ?? "—"}</td>
                    <td className="py-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLOR[c.status] ?? "bg-gray-700 text-gray-400"}`}>{c.status}</span>
                    </td>
                  </tr>
                ))}
                {companies.length === 0 && !loading && (
                  <tr><td colSpan={5} className="py-4 text-center text-gray-500">No companies found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </Layout>
  );
}
